import {
  ConfigCatProvider,
  createConsoleLogger,
  LogLevel,
  PollingMode,
  useFeatureFlag,
  User
} from "configcat-react";
import {
  createContext,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";

import { useUserContext } from "../hooks/useUserContext";
import { getEnvironment } from "../utils/environment";

/**
 * ConfigCat key for the legacy view switch. Gates whether a user may swap back
 * to the legacy view while the replacement view is rolled out. Default OFF.
 */
export const ENABLE_LEGACY_VIEW_SWITCH_FLAG_KEY = "enable_legacy_view_switch";

export type ConfigCatFlagKey = typeof ENABLE_LEGACY_VIEW_SWITCH_FLAG_KEY;

type ConfigCatFlags = Record<ConfigCatFlagKey, boolean>;

const FLAGS_OFF: ConfigCatFlags = {
  [ENABLE_LEGACY_VIEW_SWITCH_FLAG_KEY]: false
};

const ConfigCatFlagsContext = createContext<ConfigCatFlags | undefined>(
  undefined
);
const ConfigCatFlagsLoadingContext = createContext<boolean>(false);

/** Seconds between background config re-fetches, same in every environment. */
const CONFIGCAT_POLL_INTERVAL_SECONDS = 60;

const logger = createConsoleLogger(LogLevel.Warn);

type ConfigCatFlagsEvaluatorProps = Readonly<{
  email: string | null;
  onResolve: (flags: ConfigCatFlags | null) => void;
}>;

/**
 * Evaluates the rollout flag for `email` and reports the value through
 * `onResolve` (`null` while ConfigCat is still loading). Only mounted once the
 * Collins user has resolved, so ConfigCat never evaluates targeting rules
 * against a not-yet-loaded user.
 */
const ConfigCatFlagsEvaluator = memo(function ConfigCatFlagsEvaluator({
  email,
  onResolve
}: ConfigCatFlagsEvaluatorProps): null {
  const configCatUser = useMemo(
    () => (email === null ? undefined : new User(email, email)),
    [email]
  );

  const legacyViewSwitch = useFeatureFlag(
    ENABLE_LEGACY_VIEW_SWITCH_FLAG_KEY,
    false,
    configCatUser
  );

  const flags = useMemo<ConfigCatFlags | null>(
    () =>
      legacyViewSwitch.loading
        ? null
        : { [ENABLE_LEGACY_VIEW_SWITCH_FLAG_KEY]: legacyViewSwitch.value },
    [legacyViewSwitch.loading, legacyViewSwitch.value]
  );

  useEffect(() => {
    onResolve(flags);
  }, [flags, onResolve]);

  useEffect(() => () => onResolve(null), [onResolve]);

  return null;
});

/**
 * Resolves the ConfigCat SDK key from the runtime environment and, when one is
 * configured, evaluates the rollout flag inside a `ConfigCatProvider` and
 * publishes the resolved value on a context. Flags read as `false` while the
 * environment, the Collins user or ConfigCat is loading, so every consumer
 * fails closed.
 *
 * The `ConfigCatProvider` renders as a sibling evaluator rather than wrapping
 * `children`, so the tree below never remounts when the SDK key or user
 * resolves. When the environment resolves without a key, or fails to resolve,
 * there is nothing to wait for: loading is `false` and every flag reads
 * `false`.
 *
 * `useConfigCatFlag()` returns `false` for every key when this provider is
 * absent.
 */
export const ConfigCatFlagsProvider = ({
  children
}: Readonly<{ children: ReactNode }>) => {
  const { user } = useUserContext();
  const [sdkKey, setSdkKey] = useState<string | null | undefined>(undefined);
  const [resolvedFlags, setResolvedFlags] = useState<ConfigCatFlags | null>(
    null
  );

  useEffect(() => {
    let cancelled = false;
    getEnvironment()
      .then((environment) => {
        if (!cancelled) {
          setSdkKey(environment?.CONFIGCAT_SDK_KEY ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSdkKey(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const userLoaded = user !== undefined;
  const loading =
    sdkKey === undefined ||
    (sdkKey !== null && (!userLoaded || resolvedFlags === null));
  const flags = resolvedFlags ?? FLAGS_OFF;

  const evaluator =
    sdkKey && userLoaded ? (
      <ConfigCatProvider
        sdkKey={sdkKey}
        pollingMode={PollingMode.AutoPoll}
        options={{
          logger,
          pollIntervalSeconds: CONFIGCAT_POLL_INTERVAL_SECONDS
        }}
      >
        <ConfigCatFlagsEvaluator
          email={user.email ?? null}
          onResolve={setResolvedFlags}
        />
      </ConfigCatProvider>
    ) : null;

  return (
    <>
      {evaluator}
      <ConfigCatFlagsLoadingContext.Provider value={loading}>
        <ConfigCatFlagsContext.Provider value={flags}>
          {children}
        </ConfigCatFlagsContext.Provider>
      </ConfigCatFlagsLoadingContext.Provider>
    </>
  );
};

/**
 * Reads a ConfigCat flag, fail-closed (`false`) — both when ConfigCat
 * evaluates the flag as off/absent, and when no `ConfigCatFlagsProvider` is in
 * scope (e.g. unit tests, or an environment without a ConfigCat SDK key).
 *
 * @example
 * ```tsx
 * const canSwitchToLegacyView = useConfigCatFlag(ENABLE_LEGACY_VIEW_SWITCH_FLAG_KEY);
 * ```
 */
export const useConfigCatFlag = (key: ConfigCatFlagKey): boolean => {
  const flags = useContext(ConfigCatFlagsContext);

  return (flags ?? FLAGS_OFF)[key];
};

/**
 * Whether the flag values are still resolving. Every flag reads `false` until
 * they settle, so a caller whose behaviour depends on a flag can wait rather
 * than act on the fail-closed value and then act again on the real one.
 * `false` when no `ConfigCatFlagsProvider` is in scope — there is nothing to
 * wait for.
 */
export const useConfigCatFlagsLoading = (): boolean =>
  useContext(ConfigCatFlagsLoadingContext);
